import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeAIButton } from '@/shell/ForgeAIButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { Icon } from '@/components/forge/Icon'
import { AiBadge, PlainBadge, SeverityBadge, StateBadge } from '@/components/forge/StatusBadge'
import { Eyebrow, Mono, Num } from '@/components/forge/primitives'
import { assigneeNames, assigneeText, canCancel, canHold, daysUntil, nextStateOf } from '@/features/workOrders/api'
import { useUserNames } from '@/features/users/queries'
import { formatDay, formatHours } from '@/lib/format'
import { PRIORITY_TONE } from '@/lib/tone'

/**
 * One work order, opened.
 *
 * DEMO. The design's modal: badges, four facts, an advance button, delete and
 * edit. Four departures, all of them things the prototype's version cannot do.
 *
 * **The advance button is the only way to change state.** The prototype also has
 * Status as a free select in the edit form, so an order can be marked Completed
 * without ever being approved — on a screen whose subject is an approval
 * workflow. Here the workflow owns the state and the server rejects a move it
 * does not allow.
 *
 * **On Hold is its own action.** It is not anybody's successor, so it cannot be
 * reached by pressing Move: pausing work is a decision, and it gets a button
 * that says so, with Resume beside it.
 *
 * **Delete confirms.** The prototype deletes on the first click.
 *
 * **The order is connected to the rest of the platform.** Its asset, its anomaly
 * and its parts shortfall are links and facts rather than strings — the whole
 * reason this record exists is that something on a machine needs doing.
 */
export function WorkOrderDetailDialog({
  order,
  canManage,
  pending,
  onClose,
  onAdvance,
  onHold,
  onCancel,
  onEdit,
  onDelete,
}) {
  const { t } = useTranslation(['app', 'common'])
  const userNames = useUserNames()
  const [confirming, setConfirming] = useState(false)

  if (!order) return null

  const title = t(`workOrders.titles.${order.title}`, { defaultValue: order.titleText })

  return (
    <ForgeDialog
      open={Boolean(order)}
      onOpenChange={(next) => {
        if (!next) {
          setConfirming(false)
          onClose()
        }
      }}
      size="md"
      title={`${order.serialNumber} · ${title}`}
      footer={
        confirming ? (
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <p className="m-0 flex-1 text-body-sm text-ink-dim">{t('workOrders.deleteConfirm')}</p>
            <ForgeButton onClick={() => setConfirming(false)}>
              {t('common:actions.cancel')}
            </ForgeButton>
            <ForgeButton variant="danger" disabled={pending} onClick={() => onDelete(order)}>
              {t('workOrders.deleteYes')}
            </ForgeButton>
          </div>
        ) : (
          <div className="flex flex-1 flex-wrap items-center gap-2">
            {/* `work_orders.manage` alone. Somebody who may only progress the
                jobs assigned to them is not offered a control that destroys
                the record. */}
            {canManage ? (
              <ForgeButton variant="danger" onClick={() => setConfirming(true)}>
                <Icon name="trash" size="xs" />
                {t('common:actions.delete', { defaultValue: 'Delete' })}
              </ForgeButton>
            ) : null}
            <span className="ms-auto" />
            <ForgeButton onClick={onClose}>{t('common:actions.close')}</ForgeButton>
            {/* Closes the dialog: the dock it opens sits under the overlay. */}
            <ForgeAIButton action="updateWorkOrder" recordId={order.id} recordLabel={order.serialNumber} onAsk={onClose} />
            {/* Disabled until the full order has loaded: the form is a full
                replace, and a list row has no `notes` to carry back. */}
            <ForgeButton disabled={pending} onClick={() => onEdit(order)}>
              <Icon name="pencil" size="xs" />
              {t('workOrders.edit')}
            </ForgeButton>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-2">
          <StateBadge tone={PRIORITY_TONE[order.priority]} dot={false}>
            {t(`workOrders.priority.${order.priority}`)}
          </StateBadge>
          <PlainBadge>{t(`workOrders.state.${order.status}`)}</PlainBadge>
          {order.source === 'ai' ? <AiBadge label={t('workOrders.aiDrafted')} /> : null}
          {order.overdue ? (
            <StateBadge tone="red">{t('workOrders.overdue')}</StateBadge>
          ) : null}
        </div>

        <dl className="m-0 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(9rem,1fr))]">
          {/* The parent is exactly one of plant/line/machine, so the deepest
              one that resolved is the answer and the shallower two are its
              trail. `hierarchy` carries all three names with the row. */}
          <Fact label={t('workOrders.column.asset')}>
            {order.machineId ? (
              <Link
                to={`/app/assets/${order.machineId}`}
                className="text-ink no-underline hover:underline"
              >
                {order.hierarchy.machine}
              </Link>
            ) : (
              (order.hierarchy.line ?? order.hierarchy.plant ?? '—')
            )}
            <Mono className="mt-0.5 block text-micro font-normal text-ink-dim">
              {[order.hierarchy.line, order.hierarchy.plant].filter(Boolean).join(' · ')}
            </Mono>
          </Fact>
          <Fact label={t('workOrders.column.assignee')}>
            {assigneeText(assigneeNames(order, userNames), t)}
          </Fact>
          <Fact label={t('workOrders.column.due')}>
            <Num className={order.overdue ? 'text-on-red' : undefined}>
              {formatDay(order.dueDate)}
            </Num>
            <Num className="mt-0.5 block text-micro font-normal text-ink-dim">
              <DueLabel dueDate={order.dueDate} t={t} />
            </Num>
          </Fact>
          {/* Demo-only — there is no estimate column. Omitted rather than
              dashed against the real endpoint, because a fact the API does not
              hold is not a blank one. */}
          {order.demo.estimateHours ? (
            <Fact label={t('workOrders.estimate')}>
              <Num>{formatHours(order.demo.estimateHours)}</Num>
            </Fact>
          ) : null}
        </dl>

        {/* `notes` is the field the form was missing entirely: a title alone
            tells a technician what to touch and nothing about what to do. */}
        {order.notes ? (
          <div>
            <Eyebrow className="mb-1.5">{t('workOrders.notes')}</Eyebrow>
            <p className="m-0 max-w-[72ch] text-body-sm whitespace-pre-line text-ink-dim">
              {order.notes}
            </p>
          </div>
        ) : null}

        {/* The anomaly this order answers. A work order raised off an alert and
            an alert with no visible work against it are the two halves of one
            record; the prototype shows neither to the other. */}
        {order.demo.anomaly ? (
          <div className="rounded-sm border border-hairline bg-sunken p-3.5">
            <Eyebrow className="mb-1.5">{t('workOrders.raisedFrom')}</Eyebrow>
            <p className="m-0 flex flex-wrap items-center gap-2 text-body-sm">
              <SeverityBadge severity={order.demo.anomaly.severity} />
              <Mono>{order.demo.anomaly.id}</Mono>
              <span className="text-ink-dim">{order.demo.anomaly.sensor}</span>
            </p>
          </div>
        ) : null}

        {/* Why an order is stuck, derived from its own parts list rather than
            authored as a reason nobody can check. */}
        {order.demo.blockedByParts.length ? (
          <div className="rounded-sm border border-hairline bg-sunken p-3.5">
            <Eyebrow className="mb-1.5">{t('workOrders.blocked')}</Eyebrow>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {order.demo.blockedByParts.map((part) => (
                <li key={part.sku} className="flex flex-wrap items-center gap-2 text-body-sm">
                  <Mono>{part.sku}</Mono>
                  <Num className="text-ink-dim">
                    {t('workOrders.partShort', { short: part.short, onHand: part.onHand })}
                  </Num>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {order.demo.gates.length ? (
          <div>
            <Eyebrow className="mb-1.5">{t('workOrders.gates')}</Eyebrow>
            <div className="flex flex-wrap gap-1.5">
              {order.demo.gates.map((gate) => (
                <PlainBadge key={gate}>
                  <Icon name="lock" size="xs" />
                  {t(`workOrders.gate.${gate}`, { defaultValue: gate })}
                </PlainBadge>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {nextStateOf(order) ? (
            <ForgeButton variant="primary" disabled={pending} onClick={() => onAdvance(order)}>
              {t('workOrders.advanceTo', { state: t(`workOrders.state.${nextStateOf(order)}`) })}
              <Icon name="right" size="xs" className="mirror-rtl" />
            </ForgeButton>
          ) : null}
          {canHold(order) ? (
            <ForgeButton disabled={pending} onClick={() => onHold(order)}>
              {t('workOrders.putOnHold')}
            </ForgeButton>
          ) : null}
          {/* Cancelling, which is not Delete and is the point of the new column.
              The record stays, with its notes, where the next person raising
              the same job will find it. Delete is still below, and still
              confirms. */}
          {canCancel(order) ? (
            <ForgeButton disabled={pending} onClick={() => onCancel(order)}>
              {t('workOrders.cancelOrder')}
            </ForgeButton>
          ) : null}
        </div>
      </div>
    </ForgeDialog>
  )
}

function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <dt>
        <Eyebrow as="span">{label}</Eyebrow>
      </dt>
      <dd className="m-0 mt-1 text-body-sm font-semibold">{children}</dd>
    </div>
  )
}

/** Days to go, or days late. See the board's copy of this for why not an instant. */
function DueLabel({ dueDate, t }) {
  const days = daysUntil(dueDate)
  if (days === null) return t('workOrders.noDue')
  if (days === 0) return t('workOrders.dueToday')
  if (days < 0) return t('workOrders.dueOverdue', { count: -days })
  return t('workOrders.dueIn', { count: days })
}
